import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Service for managing cross-tab/window events using localStorage
 * Includes automatic cleanup of stored event keys
 */
@Injectable({
  providedIn: 'root'
})
export class StorageEventsService {
  private eventListeners: Map<string, (event: StorageEvent) => void> = new Map();
  private localSubjects: Map<string, Subject<any>> = new Map();
  
  constructor() {
    // Global listener for all storage events
    window.addEventListener('storage', (event) => this.handleStorageEvent(event));
  }

  /**
   * Emit an event to all tabs/windows, including automatic cleanup
   * @param eventKey The unique key for this event type
   * @param data Optional data to include with the event
   * @param cleanupDelay Delay in ms before cleaning up the event from localStorage (default: 500ms)
   */
  public emit(eventKey: string, data?: any, cleanupDelay: number = 500): void {
    const payload = {
      timestamp: Date.now(),
      data
    };
    
    // Store as string or as JSON depending on whether data is provided
    if (data !== undefined) {
      localStorage.setItem(eventKey, JSON.stringify(payload));
    } else {
      localStorage.setItem(eventKey, payload.timestamp.toString());
    }
    
    // Notify local subscribers
    if (this.localSubjects.has(eventKey)) {
      this.localSubjects.get(eventKey).next(payload);
    }
    
    // Clean up after delay
    setTimeout(() => {
      localStorage.removeItem(eventKey);
    }, cleanupDelay);
  }

  /**
   * Subscribe to a specific event across tabs/windows
   * @param eventKey The unique key for this event type
   * @returns Observable that emits when the event occurs
   */
  public on(eventKey: string): Observable<any> {
    // Create subject if it doesn't exist
    if (!this.localSubjects.has(eventKey)) {
      this.localSubjects.set(eventKey, new Subject<any>());
    }
    
    return this.localSubjects.get(eventKey).asObservable();
  }

  /**
   * Handle storage events from other tabs/windows
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (!event.key || !event.newValue) return;
    
    // Find the subject for this event key
    if (this.localSubjects.has(event.key)) {
      try {
        // Try to parse as JSON first
        try {
          const data = JSON.parse(event.newValue);
          this.localSubjects.get(event.key).next(data);
        } catch (e) {
          // If not valid JSON, pass the raw value
          this.localSubjects.get(event.key).next(event.newValue);
        }
      } catch (error) {
        console.error('Error handling storage event', error);
      }
    }
  }
  
  /**
   * Check if the event is recent (within specified timeframe)
   * @param timestamp The timestamp to check
   * @param maxAgeMs Maximum age in milliseconds (default: 10000ms or 10 seconds)
   */
  public isRecentEvent(timestamp: number, maxAgeMs: number = 10000): boolean {
    const now = Date.now();
    return now - timestamp < maxAgeMs;
  }
}
